import { TRIAL_MOCK_IDS } from '../constants';

const generateDailyRevenue = (
  startDate: string,
  endDate: string,
  baseCollected: number,
  baseDue: number
) => {
  const [sd, sm, sy] = startDate.split('-').map(Number);
  const [ed, em, ey] = endDate.split('-').map(Number);
  const start = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  const revenue: { date: string; collected: number; due: number }[] = [];

  const current = new Date(start);
  let dayIndex = 0;
  while (current <= end) {
    const yyyy = current.getFullYear();
    const mm = String(current.getMonth() + 1).padStart(2, '0');
    const dd = String(current.getDate()).padStart(2, '0');
    const variance = Math.sin(dayIndex * 0.7) * 0.4 + 0.8;
    revenue.push({
      date: `${yyyy}-${mm}-${dd}`,
      collected: Math.round(baseCollected * variance),
      due: Math.round(baseDue * variance * 0.6)
    });
    current.setDate(current.getDate() + 1);
    dayIndex += 1;
  }
  return revenue;
};

export const getPaymentOverviewMock = (
  timeFilterType: string,
  startDate: string,
  endDate: string
) => {
  const isPaidAt = timeFilterType === 'paid_at';

  const [sd, sm, sy] = startDate.split('-').map(Number);
  const [ed, em, ey] = endDate.split('-').map(Number);
  const start = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  const daySpan = Math.max(
    1,
    Math.round((end.getTime() - start.getTime()) / 86400000) + 1
  );
  const scale = Math.min(daySpan / 30, 1);

  return {
    collection_amount: Math.round((isPaidAt ? 245600 : 198400) * scale),
    collection_growth: isPaidAt ? 12 : 8,
    due_amount: Math.round((isPaidAt ? 34200 : 52800) * scale),
    to_be_settled: Math.round((isPaidAt ? 18500 : 22100) * scale),
    groups: 12,
    members: 156
  };
};

export const getBranchRevenueMock = (startDate: string, endDate: string) => [
  {
    branch_id: TRIAL_MOCK_IDS.BRANCH_ID,
    branch_name: TRIAL_MOCK_IDS.BRANCH_NAME,
    branch_revenue: generateDailyRevenue(startDate, endDate, 8200, 3100)
  },
  {
    branch_id: 'trial-branch-002',
    branch_name: 'North Branch',
    branch_revenue: generateDailyRevenue(startDate, endDate, 5400, 2200)
  },
  {
    branch_id: 'trial-branch-003',
    branch_name: 'South Branch',
    branch_revenue: generateDailyRevenue(startDate, endDate, 3800, 1500)
  }
];

export const getGroupPerformanceMock = (
  page: number,
  timeFilterType: string
) => {
  const isPaidAt = timeFilterType === 'paid_at';
  const allGroups = [
    {
      group_id: 'trial-group-001',
      group_name: 'Yoga Morning Batch',
      branch_id: TRIAL_MOCK_IDS.BRANCH_ID,
      branch_name: TRIAL_MOCK_IDS.BRANCH_NAME,
      amount_collected: isPaidAt ? '42500' : '38200',
      amount_due: isPaidAt ? '5600' : '9900'
    },
    {
      group_id: 'trial-group-002',
      group_name: 'Swimming Advanced',
      branch_id: TRIAL_MOCK_IDS.BRANCH_ID,
      branch_name: TRIAL_MOCK_IDS.BRANCH_NAME,
      amount_collected: isPaidAt ? '38000' : '34500',
      amount_due: isPaidAt ? '7200' : '10700'
    },
    {
      group_id: 'trial-group-003',
      group_name: 'Dance Beginners',
      branch_id: TRIAL_MOCK_IDS.BRANCH_ID,
      branch_name: TRIAL_MOCK_IDS.BRANCH_NAME,
      amount_collected: isPaidAt ? '31200' : '28900',
      amount_due: isPaidAt ? '4800' : '7100'
    },
    {
      group_id: 'trial-group-004',
      group_name: 'Karate Kids',
      branch_id: TRIAL_MOCK_IDS.BRANCH_ID,
      branch_name: TRIAL_MOCK_IDS.BRANCH_NAME,
      amount_collected: isPaidAt ? '28700' : '25400',
      amount_due: isPaidAt ? '6100' : '9400'
    },
    {
      group_id: 'trial-group-005',
      group_name: 'Music Theory',
      branch_id: TRIAL_MOCK_IDS.BRANCH_ID,
      branch_name: TRIAL_MOCK_IDS.BRANCH_NAME,
      amount_collected: isPaidAt ? '22400' : '19800',
      amount_due: isPaidAt ? '3200' : '5800'
    },
    {
      group_id: 'trial-group-006',
      group_name: 'Gym Membership',
      branch_id: 'trial-branch-002',
      branch_name: 'North Branch',
      amount_collected: isPaidAt ? '19800' : '17500',
      amount_due: isPaidAt ? '2800' : '5100'
    },
    {
      group_id: 'trial-group-007',
      group_name: 'Art Workshop',
      branch_id: 'trial-branch-002',
      branch_name: 'North Branch',
      amount_collected: isPaidAt ? '15600' : '13200',
      amount_due: isPaidAt ? '1900' : '4300'
    },
    {
      group_id: 'trial-group-008',
      group_name: 'Cricket Coaching',
      branch_id: 'trial-branch-003',
      branch_name: 'South Branch',
      amount_collected: isPaidAt ? '35200' : '31800',
      amount_due: isPaidAt ? '4500' : '7900'
    },
    {
      group_id: 'trial-group-009',
      group_name: 'Piano Classes',
      branch_id: 'trial-branch-003',
      branch_name: 'South Branch',
      amount_collected: isPaidAt ? '12800' : '10500',
      amount_due: isPaidAt ? '1700' : '3900'
    },
    {
      group_id: 'trial-group-010',
      group_name: 'Badminton Club',
      branch_id: TRIAL_MOCK_IDS.BRANCH_ID,
      branch_name: TRIAL_MOCK_IDS.BRANCH_NAME,
      amount_collected: isPaidAt ? '18300' : '16100',
      amount_due: isPaidAt ? '2400' : '4200'
    }
  ];

  const limit = 5;
  const start = page * limit;
  const paginatedData = allGroups.slice(start, start + limit);

  return {
    data: paginatedData.length > 0 ? paginatedData : null,
    pagination: {
      total_rows: allGroups.length,
      page,
      limit
    }
  };
};

export const getPaymentLinksAndModesMock = (timeFilterType: string) => {
  const isPaidAt = timeFilterType === 'paid_at';
  return {
    payment_links: {
      generated: isPaidAt ? 420 : 380,
      converted: isPaidAt ? 312 : 268
    },
    payment_modes: {
      upi: isPaidAt ? 156200 : 132400,
      cards: isPaidAt ? 42800 : 35600,
      net_banking: isPaidAt ? 18400 : 14200,
      online_mandates: isPaidAt ? 12600 : 9800,
      others: isPaidAt ? 8200 : 6400,
      offline: isPaidAt ? 7400 : 5200
    }
  };
};

export const getPaymentVolumeMock = () => {
  const data = [];
  const now = new Date();

  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const iso = d.toISOString();
    const baseOnline = 3000 + Math.round(Math.sin(i * 1.2) * 1500);
    data.push({
      day: iso,
      online: baseOnline,
      offline_upi: Math.round(baseOnline * 0.35),
      offline_bank: Math.round(baseOnline * 0.15),
      offline_cash: Math.round(baseOnline * 0.1)
    });
  }

  return { data };
};
